import {
    PRODUCT_HIGHLIGHT_POPUP_COOLDOWN_MS,
    PRODUCT_HIGHLIGHT_POPUP_STORAGE_KEY
} from "@/utils/constants.js";

/**
 * @typedef {{ nextEligibleAt: number, lastProductId: string | null }} ProductHighlightPopupState
 */

/**
 * @returns {ProductHighlightPopupState | null}
 */
export function readProductHighlightPopupState() {
    try {
        const raw = localStorage.getItem(PRODUCT_HIGHLIGHT_POPUP_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        const nextEligibleAt = Number(parsed?.nextEligibleAt);
        if (!Number.isFinite(nextEligibleAt)) {
            return null;
        }
        const lastProductId =
            typeof parsed?.lastProductId === "string" && parsed.lastProductId
                ? parsed.lastProductId
                : null;
        return { nextEligibleAt, lastProductId };
    } catch {
        return null;
    }
}

/**
 * @param {number} [now=Date.now()]
 * @returns {boolean}
 */
export function isProductHighlightPopupEligible(now = Date.now()) {
    const state = readProductHighlightPopupState();
    if (!state) {
        return true;
    }
    return now >= state.nextEligibleAt;
}

/**
 * Key used to avoid scheduling the same eligibility window twice in one document.
 * @param {ProductHighlightPopupState | null} state
 * @returns {number}
 */
export function productHighlightPopupScheduleKey(state) {
    return state?.nextEligibleAt ?? 0;
}

/**
 * @param {{ productId?: string | null, now?: number, cooldownMs?: number }} opts
 */
export function markProductHighlightPopupShown({
    productId = null,
    now = Date.now(),
    cooldownMs = PRODUCT_HIGHLIGHT_POPUP_COOLDOWN_MS
} = {}) {
    const nextEligibleAt = now + Math.max(0, cooldownMs);
    const lastProductId =
        typeof productId === "string" && productId ? productId : null;
    try {
        localStorage.setItem(
            PRODUCT_HIGHLIGHT_POPUP_STORAGE_KEY,
            JSON.stringify({ nextEligibleAt, lastProductId })
        );
    } catch {
        // Private mode / quota — popup may repeat until storage works
    }
}
